import { fingerHand, type Hand, type HandState, type Key, type Tables } from 'ErgoBot';
import type { HandLine, HandLines } from './handLines';
import type { NoteFingering } from './noteFingering';

/** Where each hand was left; null for a hand that has not played yet. */
export type HandStates = Record<Hand, HandState | null>;

export const NO_HAND_STATES: HandStates = { rh: null, lh: null };

export interface PhraseFingering {
  /** The finger number and need score of every fingered note, by drawn note. */
  fingeringByNoteId: Map<string, NoteFingering>;
  /** Where the phrase leaves each hand: the states the next phrase starts from. */
  states: HandStates;
}

interface HandFingering {
  fingerings: Array<[string, NoteFingering]>;
  state: HandState | null;
}

/** No numbers, and the next phrase places the hand afresh. */
const NOT_FINGERED: HandFingering = { fingerings: [], state: null };

const fingerHandLine = (
  tables: Tables,
  tier: string,
  hand: Hand,
  handLine: HandLine,
  state: HandState | null,
  key: Key,
): HandFingering => {
  const result = fingerHand(tables, tier, hand, { line: handLine.line, key, state });
  if (!result.ok) return NOT_FINGERED;
  return {
    fingerings: result.fingers.flatMap((finger, note): Array<[string, NoteFingering]> => {
      const noteId = handLine.noteIds[note];
      return noteId === undefined ? [] : [[noteId, { finger, need: result.need[note] }]];
    }),
    state: result.state,
  };
};

/**
 * The finger numbers of one phrase in the key it sounds in, each hand
 * continuing from where the phrase before it left that hand. A hand the tier
 * cannot finger (no tier, a staff that is not one line, notes outside the
 * hand) gets no numbers and no state.
 */
export const fingerPhraseNotes = (
  tables: Tables,
  tier: string | null,
  lines: HandLines,
  states: HandStates,
  key: Key,
): PhraseFingering => {
  const fingerOf = (hand: Hand): HandFingering => {
    const handLine = lines[hand];
    return tier === null || handLine === null
      ? NOT_FINGERED
      : fingerHandLine(tables, tier, hand, handLine, states[hand], key);
  };
  const right = fingerOf('rh');
  const left = fingerOf('lh');
  return {
    fingeringByNoteId: new Map([...right.fingerings, ...left.fingerings]),
    states: { rh: right.state, lh: left.state },
  };
};
